import React, { useState } from "react";
import {
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDropzone, Accept } from "react-dropzone";
import axios from "axios";

interface FileUploadProps {
  allowedTypes?: string[]; // Optional prop for allowed file types
  onFileUpload: (files: FileList | null) => void; // Callback to handle uploaded files
  apiEndpoint: string; // The backend API endpoint to upload the file
}

const FileUpload: React.FC<FileUploadProps> = ({
  allowedTypes,
  onFileUpload,
  apiEndpoint,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number[]>([]);
  const [uploadSuccess, setUploadSuccess] = useState<boolean | null>(null);
  const [uploadedFilesCount, setUploadedFilesCount] = useState<number>(0); // New state to track the count of uploaded files

  const accept: Accept = allowedTypes
    ? allowedTypes.reduce<Accept>((acc, type) => {
        acc[type] = [];
        return acc;
      }, {})
    : {};

  const onDrop = (acceptedFiles: File[]) => {
    const validFiles = acceptedFiles.filter(
      (file) => !allowedTypes || allowedTypes.includes(file.type)
    );
    if (validFiles.length > 0) {
      setSelectedFiles((prevFiles) => [...prevFiles, ...validFiles]);
      onFileUpload(validFiles as unknown as FileList);
    } else {
      alert("Invalid file type. Please upload valid files.");
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    accept,
  });

  const handleFileSubmit = async () => {
    if (selectedFiles.length === 0) {
      alert("No files selected");
      return;
    }

    setIsUploading(true);
    setUploadProgress(Array(selectedFiles.length).fill(0));
    setUploadSuccess(null); // Reset upload success state

    const promises = selectedFiles.map(async (file, index) => {
      const formData = new FormData();
      formData.append("file", file);

      try {
        const response = await axios.post(apiEndpoint, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const totalLength = progressEvent.total;
            if (totalLength) {
              const progress = Math.round(
                (progressEvent.loaded * 100) / totalLength
              );
              setUploadProgress((prevProgress) => {
                const newProgress = [...prevProgress];
                newProgress[index] = progress;
                return newProgress;
              });
            }
          },
        });

        console.log(`File ${file.name} uploaded successfully:`, response.data);
        setUploadedFilesCount((prevCount) => prevCount + 1); // Increment uploaded files count
        return true;
      } catch (error) {
        console.error(`Error uploading file ${file.name}:`, error);
        return false;
      }
    });

    const results = await Promise.all(promises);

    setIsUploading(false);
    setUploadSuccess(results.every((result) => result));
  };

  const handleFileRemove = (fileIndex: number) => {
    setSelectedFiles((prevFiles) =>
      prevFiles.filter((_, index) => index !== fileIndex)
    );
  };

  return (
    <Card
      variant="outlined"
      sx={{ width: "100%", maxWidth: 600, p: 2, boxShadow: 3 }}
    >
      <CardContent>
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <Box
            {...getRootProps()}
            sx={{
              width: "100%",
              maxWidth: 400,
              height: 200,
              border: "4px dashed #888",
              borderRadius: 8,
              padding: 4,
              textAlign: "center",
              cursor: "pointer",
              backgroundColor: isDragActive ? "#f0f0f0" : "#fafafa",
              transition: "background-color 0.2s ease-in-out",
              "&:hover": {
                backgroundColor: "#f0f0f0",
              },
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <input {...getInputProps()} />
            <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
              <CloudUploadIcon sx={{ fontSize: 60, color: "#888" }} />
              <Typography variant="body2" color="text.secondary">
                {isDragActive
                  ? "Drop the files here ..."
                  : "Drag & Drop files here, or click to select files"}
              </Typography>
              {selectedFiles.length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  {selectedFiles.length} file(s) selected
                </Typography>
              )}
            </Box>
          </Box>

          {selectedFiles.length > 0 && (
            <List sx={{ width: "100%", maxHeight: 200, overflow: "auto" }}>
              {selectedFiles.map((file, index) => (
                <ListItem
                  key={file.name + index}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => handleFileRemove(index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={file.name}
                    secondary={
                      isUploading ? (
                        <LinearProgress
                          variant="determinate"
                          value={uploadProgress[index]}
                          sx={{ width: "100%", mt: 1 }}
                        />
                      ) : null
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}

          <Button
            variant="contained"
            color="secondary"
            onClick={handleFileSubmit}
            disabled={isUploading || selectedFiles.length === 0}
            sx={{ textTransform: "none", width: "100%" }}
          >
            {isUploading ? <CircularProgress size={24} /> : "Submit Files"}
          </Button>

          {/* Success/Error Message */}
          {uploadSuccess === true && (
            <Typography variant="body2" color="success.main" align="center">
              {uploadedFilesCount} file(s) uploaded successfully!
            </Typography>
          )}
          {uploadSuccess === false && (
            <Typography variant="body2" color="error.main" align="center">
              Error uploading files. Try again.
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// Usage Example:
const App = () => {
  const handleFileUpload = (files: FileList | null) => {
    if (files) {
      console.log("Files selected:", files);
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      bgcolor="#f5f5f5"
    >
      <FileUpload
        allowedTypes={[
          "application/pdf",
          "application/vnd.ms-excel",
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "image/jpeg",
          "image/png",
          "image/gif",
        ]}
        onFileUpload={handleFileUpload}
        apiEndpoint="https://ultra-ubumwe-apis-app.azurewebsites.net/api/loanapplication/documents/upload" // Replace with your API endpoint
      />
    </Box>
  );
};

export default App;
